import { useState } from "react";
import { Phone, MessageCircle, Facebook, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import CookieBanner from "../components/CookieBanner";
import Reviews from "../components/Reviews";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const galleryImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_mow-quick/artifacts/1do0zhxt_Before%20and%20after_Taizemie%C5%A1i.png",
    alt: "Pirms un pēc - zāliena pļaušana"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mow-quick/artifacts/yrrm69cm_Nop%C4%BCauts.lv%20logo%20%282%29.png",
    alt: "Nopļauts.lv logo"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mow-quick/artifacts/5wa0697p_Before%20and%20after%20%282%29.png",
    alt: "Pirms un pēc - dīķa krasta pļaušana"
  },
  {
    url: "https://images.pexels.com/photos/11400230/pexels-photo-11400230.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Trimmera tuvplāns"
  },
  {
    url: "https://images.unsplash.com/photo-1458245201577-fc8a130b8829?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxsYXduJTIwbW93aW5nJTIwc2VydmljZSUyMHlhcmR8ZW58MHx8fHwxNzgwNjc5NjMxfDA&ixlib=rb-4.1.0&q=85",
    alt: "Mauriņa traktors pļauj zāli"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mow-quick/artifacts/32nz31m1_Download%20Low%20Angle%20Shot%20of%20Man%20Trimming%20Hedge%20with%20Lawnmower%20in%20Sunny%20Backyard%20for%20free.jpeg",
    alt: "Pļāvējs darbībā"
  }
];

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phoneNumber = "+37120410630";
  const whatsappLink = `https://wa.me/37120410630?text=${encodeURIComponent("Sveiki! Vēlos uzzināt vairāk par pakalpojumiem.")}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim()) {
      toast.error("Lūdzu, aizpildiet visus laukus");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success("Paldies! Mēs sazināsimies ar jums drīzumā.");
      setFormData({ name: "", phone: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Kļūda nosūtot formu. Lūdzu, mēģiniet vēlreiz.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F6F8F3" }}>
      {/* Hero Section */}
      <section className="py-24 px-8 flex flex-col items-center justify-center gap-8 text-center">
        <h1 
          className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tight" 
          style={{ color: "#1A2118" }}
          data-testid="business-name"
        >
          Nopļauts.lv
        </h1>
        <p 
          className="text-xl md:text-2xl font-medium" 
          style={{ color: "#475545" }}
          data-testid="business-slogan"
        >
          Gudrie zina, kā izbaudīt vasaru
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="whatsapp-button"
              className="text-white text-lg px-8 py-7 rounded-full font-bold shadow-lg hover:scale-105 transition-transform duration-200"
              style={{ backgroundColor: "#25D366" }}
            >
              <MessageCircle className="mr-2 h-6 w-6" />
              Rakstīt WhatsApp
            </Button>
          </a>
          <a href={`tel:${phoneNumber}`}>
            <Button
              data-testid="call-button"
              className="text-white text-lg px-8 py-7 rounded-full font-bold shadow-lg hover:scale-105 transition-transform duration-200"
              style={{ backgroundColor: "#27A64C" }}
            >
              <Phone className="mr-2 h-6 w-6" />
              Zvanīt tagad
            </Button>
          </a>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-8">
        <div className="max-w-md mx-auto text-center mb-8">
          <h2 
            className="text-3xl font-bold mb-3" 
            style={{ color: "#1A2118" }}
            data-testid="contact-form-heading"
          >
            Saņem bezmaksas piedāvājumu!
          </h2>
          <p style={{ color: "#475545" }}>
            Atstājiet savu kontaktinformāciju un mēs sazināsimies ar jums!
          </p>
        </div>
        <div className="flex justify-center">
          <Card 
          className="p-8 max-w-md w-full shadow-xl" 
          style={{ backgroundColor: "#FFFFFF", borderRadius: "24px" }}
          data-testid="contact-form-card"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-medium mb-2"
                style={{ color: "#1A2118" }}
              >
                Vārds
              </label>
              <Input
                id="name"
                data-testid="contact-form-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jūsu vārds"
                className="w-full rounded-2xl"
                style={{ borderColor: "rgba(26, 33, 24, 0.08)" }}
              />
            </div>
            <div>
              <label 
                htmlFor="phone" 
                className="block text-sm font-medium mb-2"
                style={{ color: "#1A2118" }}
              >
                Tālrunis
              </label>
              <Input
                id="phone"
                data-testid="contact-form-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+371..."
                className="w-full rounded-2xl"
                style={{ borderColor: "rgba(26, 33, 24, 0.08)" }}
              />
            </div>
            <Button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={isSubmitting}
              className="w-full text-white text-base py-6 rounded-full font-bold hover:scale-[1.02] transition-transform duration-200"
              style={{ backgroundColor: "#27A64C" }}
            >
              {isSubmitting ? "Nosūta..." : "Saņemt piedāvājumu"}
            </Button>
            <p className="text-xs text-center" style={{ color: "#475545" }} data-testid="consent-text">
              Nosūtot formu, piekrītu manu datu apstrādei saskaņā ar{" "}
              <a
                href="/privatuma-politika"
                className="underline"
                style={{ color: "#27A64C" }}
              >
                privātuma politiku
              </a>
              .
            </p>
          </form>
        </Card>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-8" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                data-testid={`gallery-image-${index}`}
                className="overflow-hidden rounded-3xl hover:scale-[1.03] transition-transform duration-300 shadow-lg"
                style={{ aspectRatio: "1/1" }}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <Reviews />

      {/* Footer */}
      <footer className="py-12 px-8 border-t" style={{ borderColor: "rgba(26, 33, 24, 0.08)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:justify-between gap-8 text-sm" style={{ color: "#475545" }}>
          <div data-testid="footer-company">
            <p className="font-bold mb-1" style={{ color: "#1A2118" }}>SIA "MFM Solutions"</p>
            <p>Vēju iela "Filipi", Iecava,</p>
            <p>Bauskas novads, LV-3913</p>
            <p className="mt-2">Reģ. Nr. 40203672460</p>
          </div>

          <div className="flex flex-col gap-2" data-testid="footer-contact">
            <p className="font-bold mb-1" style={{ color: "#1A2118" }}>Kontakti</p>
            <a 
              href={`tel:${phoneNumber}`}
              className="hover:underline"
              data-testid="footer-phone"
            >
              {phoneNumber}
            </a>
            <a 
              href="mailto:info@noplauts.lv"
              className="hover:underline"
              data-testid="footer-email"
            >
              info@noplauts.lv
            </a>
            <a 
              href="/privatuma-politika" 
              className="hover:underline mt-1"
              data-testid="privacy-policy-link"
            >
              Privātuma politika
            </a>
          </div>

          <div className="flex flex-col gap-3" data-testid="footer-social">
            <p className="font-bold mb-1" style={{ color: "#1A2118" }}>Seko mums</p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/profile.php?q=Nop%C4%BCauts.lv%20-%20p%C4%BCau%C5%A1anas%20pakalpojumi"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: "#27A64C" }}
              >
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a
                href="https://www.instagram.com/noplauts.lv"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-200"
                style={{ backgroundColor: "#27A64C" }}
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 text-center text-xs" style={{ color: "#475545" }}>
          <p>© 2026 Nopļauts.lv · SIA "MFM Solutions". Visas tiesības aizsargātas.</p>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
};

export default LandingPage;